/**
 * Everything currently wrong, in one list, above the grid.
 *
 * A wall of cards makes an operator hunt for the failing one. This is the answer to
 * "what do I look at first": every failing check across every target, critical ones
 * ahead of non-critical, with the detail the check itself wrote.
 *
 * It renders nothing when nothing is wrong - an "all clear" banner on a healthy estate
 * is chrome that trains people to skip the region where real problems will appear.
 */

import { ShieldAlert } from 'lucide-react'

import type { Rollup, TargetResult } from '../api/types'
import { cn } from '../lib/cn'
import { statusText, STATUS_PRESENTATION } from '../lib/status'

interface AttentionPanelProps {
  rollup: Rollup
  results: TargetResult[]
  className?: string
}

export function AttentionPanel({ rollup, results, className }: AttentionPanelProps) {
  // A target that never answered has no failing checks to list, but is the most urgent
  // thing on the page - so it is surfaced here in its own right.
  const unreachable = results.filter((result) => !result.reachable)

  const failing = [...rollup.degraded_services].sort((a, b) => {
    const weight = (entry: typeof a) => (entry.important === false ? 1 : 0)
    return weight(a) - weight(b) || a.site_name.localeCompare(b.site_name)
  })

  if (failing.length === 0 && unreachable.length === 0) return null

  return (
    <section
      className={cn('card p-4', className)}
      aria-label="Needs attention"
      style={{ borderLeftWidth: 3, borderLeftColor: 'var(--color-critical)' }}
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
        <ShieldAlert size={15} aria-hidden className={statusText('critical')} />
        Needs attention
      </h2>

      <ul className="mt-3 space-y-2">
        {unreachable.map((result) => {
          const presentation = STATUS_PRESENTATION[result.status]
          const Icon = presentation.icon
          return (
            <li key={result.target_id} className="flex items-start gap-2 text-xs">
              <Icon
                size={13}
                aria-hidden
                className={cn('mt-0.5 shrink-0', statusText(presentation.token))}
              />
              <div className="min-w-0">
                <p className="font-medium text-ink">
                  {result.site_name} · {result.env_label}
                  <span className="ml-1.5 font-normal text-ink-muted">{result.host}</span>
                </p>
                <p className="break-words text-ink-secondary">
                  {result.error ?? presentation.description}
                </p>
              </div>
            </li>
          )
        })}

        {failing.map((entry) => (
          <li
            key={`${entry.target_id}-${entry.service}`}
            className="flex items-start gap-2 text-xs"
          >
            <span
              aria-hidden
              className={cn(
                'mt-1 inline-block h-2 w-2 shrink-0 rounded-full',
                entry.important === false ? 'bg-warning' : 'bg-critical',
              )}
            />
            <div className="min-w-0">
              <p className="font-medium text-ink">
                {entry.site_name} · {entry.env_label}
                <span className="ml-1.5 text-critical">{entry.service}</span>
                {entry.important === false ? (
                  <span className="ml-1.5 font-normal text-ink-muted">non-critical</span>
                ) : null}
              </p>
              <p className="break-words text-ink-secondary">
                {entry.detail ?? 'Reported down. Detail needs an API key.'}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
